<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { X, Check, AlertCircle, Loader2, Settings, FileText, Clock, HardDrive, CloudOff, History, FileAudio, Infinity, Sparkles, Video, Brain, Star, Users, CloudCog, Download } from 'lucide-vue-next'
import { useSubscriptionStore, TIER_CONFIG, type SubscriptionTier } from '../stores/subscription'

const { t } = useI18n()

const props = defineProps<{
  show: boolean
}>()

const emit = defineEmits<{
  close: []
}>()

const subscriptionStore = useSubscriptionStore()

// Checkout state
const checkoutLoading = ref<SubscriptionTier | null>(null)
const checkoutError = ref('')
const portalLoading = ref(false)

// Get feature icon component
function getFeatureIcon(iconName: string) {
  const icons: Record<string, any> = {
    FileText, Clock, HardDrive, CloudOff, History, FileAudio,
    Infinity, Sparkles, Video, Brain, Star, Users, CloudCheck: CloudCog, Download
  }
  return icons[iconName] || FileText
}

// Feature type for template usage
type TierFeature = { icon: string; key: string; params?: Record<string, unknown> }

// Get features for a tier from TIER_CONFIG (single source of truth)
function getTierFeatures(tier: SubscriptionTier): TierFeature[] {
  return [...TIER_CONFIG[tier].features] as TierFeature[]
}

// Get button text translation key for a tier
function getButtonTextKey(tier: SubscriptionTier): string {
  const buttonKeys: Record<SubscriptionTier, string> = {
    free: 'subscription.buttons.getStartedFree',
    basic: 'subscription.buttons.chooseSpark',
    standard: 'subscription.buttons.getMemory',
    premium: 'subscription.buttons.chooseIntel'
  }
  return buttonKeys[tier]
}

// Tier order for comparison (higher index = higher tier)
const tierOrder: SubscriptionTier[] = ['free', 'basic', 'standard', 'premium']

// Compare tiers: returns 'upgrade' if targetTier > currentTier, 'downgrade' if targetTier < currentTier, 'current' if same
function compareTiers(targetTier: SubscriptionTier, currentTier: SubscriptionTier): 'upgrade' | 'downgrade' | 'current' {
  const targetIndex = tierOrder.indexOf(targetTier)
  const currentIndex = tierOrder.indexOf(currentTier)
  if (targetIndex > currentIndex) return 'upgrade'
  if (targetIndex < currentIndex) return 'downgrade'
  return 'current'
}

// Reset state when component shows
watch(() => props.show, async (show) => {
  if (show) {
    checkoutError.value = ''
    // Refresh subscription status when dialog opens
    await subscriptionStore.fetchStatus()
  }
}, { immediate: true })

function closeDialog() {
  emit('close')
}

// Start Stripe checkout for a tier
async function startCheckout(tier: SubscriptionTier) {
  if (checkoutLoading.value || tier === 'free') return

  checkoutLoading.value = tier
  checkoutError.value = ''

  const result = await subscriptionStore.createCheckout(tier)

  if ('error' in result) {
    checkoutError.value = result.error
    checkoutLoading.value = null
  } else {
    // Redirect to Stripe Checkout
    window.location.href = result.url
  }
}

// Open Stripe customer portal
async function openPortal() {
  if (portalLoading.value) return

  portalLoading.value = true
  checkoutError.value = ''

  const result = await subscriptionStore.openCustomerPortal()

  if ('error' in result) {
    checkoutError.value = result.error
    portalLoading.value = false
  } else {
    window.location.href = result.url
  }
}

// Get formatted price
function getFormattedPrice(tier: SubscriptionTier): string {
  const config = TIER_CONFIG[tier]
  if (tier === 'free') return '€0'
  return `€${config.priceMonthly.toFixed(2)}`
}

// Get price period text
function getPricePeriod(tier: SubscriptionTier): string {
  if (tier === 'free') return t('subscription.forever')
  return t('subscription.perMonth')
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4"
      @click.self="closeDialog"
    >
      <div class="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-dark-bg dark:to-dark-surface rounded-2xl shadow-2xl dark:shadow-none dark:border dark:border-dark-border max-w-5xl w-full overflow-hidden max-h-[95vh] overflow-y-auto">
        <!-- Header -->
        <div class="px-6 pt-4 pb-2">
          <div class="flex items-center justify-between mb-1">
            <div></div>
            <button
              @click="closeDialog"
              class="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
            >
              <X class="w-6 h-6" />
            </button>
          </div>

          <!-- Title -->
          <div class="text-center mb-4">
            <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {{ $t('subscription.choosePlan') }}
            </h2>
            <p class="text-lg text-gray-600 dark:text-gray-400">
              {{ $t('subscription.neverForget') }}
            </p>
          </div>

        </div>

        <!-- Plans Content -->
        <div class="px-6 pb-4">
          <div>
            <!-- Subscription management notices -->
            <div v-if="subscriptionStore.canManageOnWeb" class="mb-4 p-3 bg-white dark:bg-dark-elevated rounded-xl border border-gray-200 dark:border-dark-border shadow-sm">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm text-gray-600 dark:text-gray-300">
                    <span class="font-semibold">{{ $t('subscription.currentPlan') }}:</span> {{ subscriptionStore.tierLabel }}
                  </p>
                  <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {{ $t('subscription.managePlan') }}
                  </p>
                </div>
                <button
                  @click="openPortal"
                  :disabled="portalLoading"
                  class="inline-flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-800 dark:bg-dark-border dark:hover:bg-dark-border-hover text-white text-sm font-medium rounded-lg transition-colors"
                >
                  <Loader2 v-if="portalLoading" class="w-4 h-4 animate-spin" />
                  <Settings v-else class="w-4 h-4" />
                  {{ portalLoading ? $t('subscription.opening') : $t('subscription.manage') }}
                </button>
              </div>
            </div>

            <div v-else-if="subscriptionStore.canManageOnIOS" class="mb-4 p-3 bg-blue-50 dark:bg-ios-blue/10 rounded-xl">
              <p class="text-sm text-blue-700 dark:text-ios-blue text-center">
                {{ $t('subscription.iosManageNote') }}
              </p>
            </div>

            <!-- Tier Cards Grid -->
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <!-- FREE Tier -->
              <div class="bg-white dark:bg-dark-elevated rounded-2xl overflow-hidden border border-gray-200 dark:border-dark-border shadow-sm hover:shadow-md transition-shadow flex flex-col">
                <!-- Header with solid color -->
                <div class="bg-gray-100 dark:bg-dark-border/50 pt-4 px-4 pb-4">
                  <div class="text-center mb-1">
                    <h3 class="text-sm font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">{{ $t('subscription.tiers.free.name') }}</h3>
                  </div>
                  <div class="text-center">
                    <span class="text-3xl font-bold text-gray-400 dark:text-gray-500">€0</span>
                    <span class="text-gray-400 dark:text-gray-500 text-sm ml-1">/ {{ $t('subscription.forever') }}</span>
                  </div>
                </div>
                <div class="px-4 pb-4 flex flex-col flex-grow">
                  <ul class="space-y-2 mb-4 flex-grow">
                    <li v-for="feature in getTierFeatures('free')" :key="feature.key" class="flex items-start gap-3">
                      <component :is="getFeatureIcon(feature.icon)" class="w-5 h-5 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0" />
                      <span class="text-sm text-gray-600 dark:text-gray-400">{{ $t(feature.key, feature.params || {}) }}</span>
                    </li>
                  </ul>
                  <button
                    v-if="subscriptionStore.tier === 'free'"
                    class="w-full py-3 px-4 bg-gray-100 dark:bg-dark-border text-gray-500 dark:text-gray-400 font-medium rounded-full cursor-default"
                  >
                    {{ $t('subscription.currentPlan') }}
                  </button>
                  <!-- For paid users, show Downgrade button that opens portal -->
                  <button
                    v-else-if="subscriptionStore.canManageOnWeb"
                    @click="openPortal"
                    :disabled="portalLoading"
                    class="w-full py-3 px-4 bg-gray-100 dark:bg-dark-border text-gray-600 dark:text-gray-400 font-medium rounded-full hover:bg-gray-200 dark:hover:bg-dark-border-hover transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Loader2 v-if="portalLoading" class="w-4 h-4 animate-spin" />
                    {{ portalLoading ? $t('common.loading') : $t('subscription.buttons.downgrade') }}
                  </button>
                  <button
                    v-else
                    class="w-full py-3 px-4 bg-gray-100 dark:bg-dark-border text-gray-600 dark:text-gray-400 font-medium rounded-full cursor-default"
                  >
                    {{ $t(getButtonTextKey('free')) }}
                  </button>
                </div>
              </div>

              <!-- SPARK Tier -->
              <div class="bg-white dark:bg-dark-elevated rounded-2xl overflow-hidden border border-gray-200 dark:border-dark-border shadow-sm hover:shadow-md transition-shadow flex flex-col">
                <!-- Header with solid color -->
                <div class="bg-teal-50 dark:bg-teal-900/30 pt-4 px-4 pb-4">
                  <div class="text-center mb-1">
                    <h3 class="text-sm font-semibold text-teal-600 dark:text-teal-400 uppercase tracking-wider">{{ $t('subscription.tiers.basic.name') }}</h3>
                  </div>
                  <div class="text-center">
                    <span class="text-3xl font-bold text-teal-600 dark:text-teal-400">{{ getFormattedPrice('basic') }}</span>
                    <span class="text-teal-500 dark:text-teal-500 text-sm ml-1">{{ getPricePeriod('basic') }}</span>
                  </div>
                </div>
                <div class="px-4 pb-4 flex flex-col flex-grow">
                  <ul class="space-y-2 mb-4 flex-grow">
                    <li v-for="feature in getTierFeatures('basic')" :key="feature.key" class="flex items-start gap-3">
                      <component :is="getFeatureIcon(feature.icon)" class="w-5 h-5 text-teal-500 dark:text-teal-400 mt-0.5 flex-shrink-0" />
                      <span class="text-sm text-gray-600 dark:text-gray-400">{{ $t(feature.key, feature.params || {}) }}</span>
                    </li>
                  </ul>
                  <!-- Current plan: show with Cancel button -->
                  <div v-if="subscriptionStore.tier === 'basic'" class="space-y-2">
                    <div class="w-full py-3 px-4 bg-teal-100 dark:bg-teal-500/20 text-teal-600 dark:text-teal-400 font-medium rounded-full flex items-center justify-center gap-2">
                      <Check class="w-4 h-4" />
                      {{ $t('subscription.currentPlan') }}
                    </div>
                    <button
                      v-if="subscriptionStore.canManageOnWeb"
                      @click="openPortal"
                      :disabled="portalLoading"
                      class="w-full py-2 px-4 text-red-500 dark:text-red-400 text-sm font-medium hover:bg-red-50 dark:hover:bg-red-500/10 rounded-full transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <Loader2 v-if="portalLoading" class="w-4 h-4 animate-spin" />
                      {{ portalLoading ? $t('common.loading') : $t('subscription.buttons.cancel') }}
                    </button>
                  </div>
                  <!-- For paid users on higher tier: Downgrade button -->
                  <button
                    v-else-if="subscriptionStore.isPaid && compareTiers('basic', subscriptionStore.tier) === 'downgrade'"
                    @click="openPortal"
                    :disabled="portalLoading"
                    class="w-full py-3 px-4 border-2 border-teal-400 dark:border-teal-500 text-teal-600 dark:text-teal-400 font-medium rounded-full hover:bg-teal-50 dark:hover:bg-teal-500/10 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Loader2 v-if="portalLoading" class="w-4 h-4 animate-spin" />
                    {{ portalLoading ? $t('common.loading') : $t('subscription.buttons.downgrade') }}
                  </button>
                  <!-- For free users: Subscribe/Checkout button -->
                  <button
                    v-else
                    @click="startCheckout('basic')"
                    :disabled="checkoutLoading !== null"
                    class="w-full py-3 px-4 border-2 border-teal-400 dark:border-teal-500 text-teal-600 dark:text-teal-400 font-medium rounded-full hover:bg-teal-50 dark:hover:bg-teal-500/10 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Loader2 v-if="checkoutLoading === 'basic'" class="w-4 h-4 animate-spin" />
                    {{ checkoutLoading === 'basic' ? $t('common.loading') : $t('subscription.buttons.subscribe') }}
                  </button>
                </div>
              </div>

              <!-- MEMORY Tier (Most Popular) -->
              <div class="bg-white dark:bg-dark-elevated rounded-2xl overflow-hidden border-2 border-purple-400 dark:border-purple-500 shadow-lg shadow-purple-500/10 hover:shadow-xl hover:shadow-purple-500/20 transition-all flex flex-col relative transform lg:scale-105">
                <!-- Most Popular Badge -->
                <div class="bg-gradient-to-r from-purple-600 to-purple-700 text-white text-center py-1.5 px-4">
                  <span class="text-xs font-bold uppercase tracking-wider">{{ $t('subscription.mostPopular') }}</span>
                </div>
                <!-- Header with solid color -->
                <div class="bg-purple-50 dark:bg-purple-900/30 pt-3 px-4 pb-4">
                  <div class="text-center mb-1">
                    <h3 class="text-sm font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wider">{{ $t('subscription.tiers.standard.name') }}</h3>
                  </div>
                  <div class="text-center">
                    <span class="text-3xl font-bold text-purple-600 dark:text-purple-400">{{ getFormattedPrice('standard') }}</span>
                    <span class="text-purple-500 dark:text-purple-400 text-sm ml-1">{{ getPricePeriod('standard') }}</span>
                  </div>
                </div>
                <div class="px-4 pb-4 flex flex-col flex-grow">
                  <ul class="space-y-2 mb-4 flex-grow">
                    <li v-for="feature in getTierFeatures('standard')" :key="feature.key" class="flex items-start gap-3">
                      <component :is="getFeatureIcon(feature.icon)" class="w-5 h-5 text-purple-500 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                      <span class="text-sm text-gray-700 dark:text-gray-300">{{ $t(feature.key, feature.params || {}) }}</span>
                    </li>
                  </ul>
                  <!-- Current plan: show with Cancel button -->
                  <div v-if="subscriptionStore.tier === 'standard'" class="space-y-2">
                    <div class="w-full py-3 px-4 bg-purple-100 dark:bg-purple-500/20 text-purple-600 dark:text-purple-400 font-medium rounded-full flex items-center justify-center gap-2">
                      <Check class="w-4 h-4" />
                      {{ $t('subscription.currentPlan') }}
                    </div>
                    <button
                      v-if="subscriptionStore.canManageOnWeb"
                      @click="openPortal"
                      :disabled="portalLoading"
                      class="w-full py-2 px-4 text-red-500 dark:text-red-400 text-sm font-medium hover:bg-red-50 dark:hover:bg-red-500/10 rounded-full transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <Loader2 v-if="portalLoading" class="w-4 h-4 animate-spin" />
                      {{ portalLoading ? $t('common.loading') : $t('subscription.buttons.cancel') }}
                    </button>
                  </div>
                  <!-- For paid users on higher tier: Downgrade button -->
                  <button
                    v-else-if="subscriptionStore.isPaid && compareTiers('standard', subscriptionStore.tier) === 'downgrade'"
                    @click="openPortal"
                    :disabled="portalLoading"
                    class="w-full py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-full transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Loader2 v-if="portalLoading" class="w-4 h-4 animate-spin" />
                    {{ portalLoading ? $t('common.loading') : $t('subscription.buttons.downgrade') }}
                  </button>
                  <!-- For paid users on lower tier: Upgrade button -->
                  <button
                    v-else-if="subscriptionStore.isPaid && compareTiers('standard', subscriptionStore.tier) === 'upgrade'"
                    @click="openPortal"
                    :disabled="portalLoading"
                    class="w-full py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-full transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Loader2 v-if="portalLoading" class="w-4 h-4 animate-spin" />
                    {{ portalLoading ? $t('common.loading') : $t('subscription.buttons.upgrade') }}
                  </button>
                  <!-- For free users: Subscribe/Checkout button -->
                  <button
                    v-else
                    @click="startCheckout('standard')"
                    :disabled="checkoutLoading !== null"
                    class="w-full py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-full transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Loader2 v-if="checkoutLoading === 'standard'" class="w-4 h-4 animate-spin" />
                    {{ checkoutLoading === 'standard' ? $t('common.loading') : $t('subscription.buttons.subscribe') }}
                  </button>
                </div>
              </div>

              <!-- INTEL Tier -->
              <div class="bg-white dark:bg-dark-elevated rounded-2xl overflow-hidden border border-gray-300 dark:border-dark-border shadow-sm hover:shadow-md transition-shadow flex flex-col">
                <div class="bg-slate-200 dark:bg-slate-800/30 py-1.5 px-4">
                  <span class="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider block text-center">{{ $t('subscription.tiers.premium.subtitle') }}</span>
                </div>
                <!-- Header with solid color -->
                <div class="bg-slate-100 dark:bg-slate-800/30 pt-3 px-4 pb-4">
                  <div class="text-center mb-1">
                    <h3 class="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">{{ $t('subscription.tiers.premium.name') }}</h3>
                  </div>
                  <div class="text-center">
                    <span class="text-3xl font-bold text-slate-700 dark:text-slate-300">{{ getFormattedPrice('premium') }}</span>
                    <span class="text-slate-500 dark:text-slate-400 text-sm ml-1">{{ getPricePeriod('premium') }}</span>
                  </div>
                </div>
                <div class="px-4 pb-4 flex flex-col flex-grow">
                  <ul class="space-y-2 mb-4 flex-grow">
                    <li v-for="feature in getTierFeatures('premium')" :key="feature.key" class="flex items-start gap-3">
                      <component :is="getFeatureIcon(feature.icon)" class="w-5 h-5 text-slate-500 dark:text-slate-400 mt-0.5 flex-shrink-0" />
                      <span class="text-sm text-gray-600 dark:text-gray-400">{{ $t(feature.key, feature.params || {}) }}</span>
                    </li>
                  </ul>
                  <!-- Current plan: show with Cancel button -->
                  <div v-if="subscriptionStore.tier === 'premium'" class="space-y-2">
                    <div class="w-full py-3 px-4 bg-slate-200 dark:bg-slate-700/30 text-slate-600 dark:text-slate-400 font-medium rounded-full flex items-center justify-center gap-2">
                      <Check class="w-4 h-4" />
                      {{ $t('subscription.currentPlan') }}
                    </div>
                    <button
                      v-if="subscriptionStore.canManageOnWeb"
                      @click="openPortal"
                      :disabled="portalLoading"
                      class="w-full py-2 px-4 text-red-500 dark:text-red-400 text-sm font-medium hover:bg-red-50 dark:hover:bg-red-500/10 rounded-full transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <Loader2 v-if="portalLoading" class="w-4 h-4 animate-spin" />
                      {{ portalLoading ? $t('common.loading') : $t('subscription.buttons.cancel') }}
                    </button>
                  </div>
                  <!-- For paid users on lower tier: Upgrade button -->
                  <button
                    v-else-if="subscriptionStore.isPaid && compareTiers('premium', subscriptionStore.tier) === 'upgrade'"
                    @click="openPortal"
                    :disabled="portalLoading"
                    class="w-full py-3 px-4 border-2 border-slate-400 dark:border-slate-500 text-slate-700 dark:text-slate-300 font-medium rounded-full hover:bg-slate-50 dark:hover:bg-slate-500/10 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Loader2 v-if="portalLoading" class="w-4 h-4 animate-spin" />
                    {{ portalLoading ? $t('common.loading') : $t('subscription.buttons.upgrade') }}
                  </button>
                  <!-- For free users: Subscribe/Checkout button -->
                  <button
                    v-else
                    @click="startCheckout('premium')"
                    :disabled="checkoutLoading !== null"
                    class="w-full py-3 px-4 border-2 border-slate-400 dark:border-slate-500 text-slate-700 dark:text-slate-300 font-medium rounded-full hover:bg-slate-50 dark:hover:bg-slate-500/10 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Loader2 v-if="checkoutLoading === 'premium'" class="w-4 h-4 animate-spin" />
                    {{ checkoutLoading === 'premium' ? $t('common.loading') : $t('subscription.buttons.subscribe') }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Checkout error -->
            <div v-if="checkoutError" class="mt-6 flex items-start gap-2 text-red-600 dark:text-ios-red p-4 bg-red-50 dark:bg-ios-red/10 rounded-xl">
              <AlertCircle class="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span class="text-sm">{{ checkoutError }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
